import React from 'react';
import FormField from '../shared/FormField';
import CountrySelect from '../shared/CountrySelect';
import PhoneInput from '../shared/PhoneInput';
import EmailInput from '../shared/EmailInput';
import formDataStorage from '../../utils/formDataStorage';
import { Plus, Trash2, User, MapPin, Phone, Mail } from 'lucide-react';

// F1签证额外联系人信息表单
const F1AdditionalContactsForm = ({ data, onChange }) => {

  // 从formDataStorage读取数据并确保数据同步
  React.useEffect(() => {
    console.log('F1AdditionalContactsForm useEffect - 初始化数据检查');
    const savedData = formDataStorage.loadFormData('f1_additional_contacts');
    console.log('F1AdditionalContactsForm useEffect - 已保存数据:', savedData);
    console.log('F1AdditionalContactsForm useEffect - 当前props数据:', data);
    
    // 如果有保存的数据但当前data为空或部分字段缺失，则从数据库加载
    if (savedData && (!data || Object.keys(data).length === 0)) {
      console.log('F1AdditionalContactsForm useEffect - 从数据库加载数据');
      Object.keys(savedData).forEach(key => {
        if (savedData[key] !== null && savedData[key] !== undefined) {
          onChange(key, savedData[key]);
        }
      });
    }
    
    // 确保至少有两个联系人（F1签证要求）
    const currentContacts = data.additional_contacts || savedData?.additional_contacts || [];
    if (currentContacts.length < 2) {
      console.log('F1AdditionalContactsForm useEffect - 初始化默认联系人（至少两个）');
      console.log('F1AdditionalContactsForm useEffect - 当前联系人数量:', currentContacts.length);
      
      const defaultContact = {
        surnames: "",
        given_name: "",
        street_address_line1: "",
        street_address_line2: "",
        city: "",
        state_province: "",
        state_province_na: false,
        postal_code: "",
        postal_code_na: false,
        country: "",
        telephone: "",
        telephone_na: false,
        email: "",
        email_na: false
      };
      
      // 计算需要添加的联系人数量
      const contactsNeeded = 2 - currentContacts.length;
      const contactsToAdd = Array(contactsNeeded).fill(null).map(() => ({ ...defaultContact }));
      
      console.log('F1AdditionalContactsForm useEffect - 需要添加联系人数量:', contactsNeeded);
      console.log('F1AdditionalContactsForm useEffect - 最终联系人列表:', [...currentContacts, ...contactsToAdd]);
      
      onChange('additional_contacts', [...currentContacts, ...contactsToAdd]);
    }
  }, [data, onChange]);

  // 添加新联系人
  const addContact = () => {
    const currentContacts = data.additional_contacts || [];
    const newContact = {
      surnames: "",
      given_name: "",
      street_address_line1: "",
      street_address_line2: "",
      city: "",
      state_province: "",
      state_province_na: false,
      postal_code: "",
      postal_code_na: false,
      country: "",
      telephone: "",
      telephone_na: false,
      email: "",
      email_na: false
    };
    onChange('additional_contacts', [...currentContacts, newContact]);
  };

  // 删除联系人
  const removeContact = (index) => {
    const currentContacts = data.additional_contacts || [];
    // 确保至少保留两个联系人（F1签证要求）
    if (currentContacts.length > 2) {
      const newContacts = currentContacts.filter((_, i) => i !== index);
      onChange('additional_contacts', newContacts);
    }
  };

  // 更新联系人信息
  const updateContact = (index, field, value) => {
    const currentContacts = data.additional_contacts || [];
    const newContacts = [...currentContacts];
    
    if (!newContacts[index]) {
      newContacts[index] = {
        surnames: "",
        given_name: "",
        street_address_line1: "",
        street_address_line2: "",
        city: "",
        state_province: "",
        state_province_na: false,
        postal_code: "",
        postal_code_na: false,
        country: "",
        telephone: "",
        telephone_na: false,
        email: "",
        email_na: false
      };
    }

    // 处理"不适用"复选框的逻辑
    if (field.endsWith('_na')) {
      newContacts[index] = { 
        ...newContacts[index], 
        [field]: value,
        // 如果选择了"不适用"，清空对应字段
        [field.replace('_na', '')]: value ? "" : newContacts[index][field.replace('_na', '')]
      };
    } else {
      newContacts[index] = { ...newContacts[index], [field]: value };
    }

    onChange('additional_contacts', newContacts);
  };

  return (
    <div className="space-y-6">
      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 p-6 rounded-lg">
        <h3 className="text-2xl font-bold text-gray-800 mb-6 flex items-center">
          <span className="w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center mr-3 text-sm">👥</span>
          F1签证额外联系人信息
        </h3>

        <div className="bg-white p-6 rounded-lg shadow-sm mb-6">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h4 className="text-lg font-semibold text-gray-800 flex items-center">
                <User className="w-5 h-5 mr-2" />
                额外联系人信息 (Additional Contacts)
              </h4>
              <p className="text-sm text-gray-600 mt-1">
                请提供在美国的额外联系人信息，包括学术顾问、导师或其他重要联系人（F1签证要求至少两个联系人）
              </p>
            </div>
            <button
              type="button"
              onClick={addContact}
              className="flex items-center px-4 py-2 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-lg hover:from-blue-700 hover:to-blue-800 transition-all duration-200 text-sm font-medium shadow-sm hover:shadow-md"
            >
              <Plus className="w-4 h-4 mr-1" />
              添加联系人
            </button>
          </div>

          <div className="space-y-6">
            {(() => {
              const contacts = data.additional_contacts || [];
              return contacts.map((contact, index) => (
                <div key={index} className="p-6 bg-gradient-to-r from-gray-50 to-blue-50 rounded-lg border-l-4 border-blue-400 shadow-sm">
                  <div className="flex items-center justify-between mb-4">
                    <h5 className="text-lg font-semibold text-blue-800 flex items-center">
                      <User className="w-4 h-4 mr-2" />
                      联系人 #{index + 1}
                    </h5>
                    {contacts.length > 2 && (
                      <button
                        type="button"
                        onClick={() => removeContact(index)}
                        className="flex items-center justify-center w-8 h-8 bg-gradient-to-r from-red-500 to-red-600 text-white rounded-lg hover:from-red-600 hover:to-red-700 transition-all duration-200 shadow-sm hover:shadow-md"
                        title="删除此联系人"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </div>

                  <div className="space-y-4">
                    {/* 姓名信息 */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <FormField
                        label="姓氏 (Surnames)"
                        value={contact.surnames || ''}
                        onChange={(e) => updateContact(index, 'surnames', e.target.value.slice(0, 33))}
                        placeholder="Zhang"
                        maxLength={33}
                        required
                      />
                      <FormField
                        label="名字 (Given Names)"
                        value={contact.given_name || ''}
                        onChange={(e) => updateContact(index, 'given_name', e.target.value.slice(0, 33))}
                        placeholder="Wei"
                        maxLength={33}
                        required
                      />
                    </div>

                    {/* 地址信息 */}
                    <div className="p-4 bg-gradient-to-r from-green-50 to-emerald-50 rounded-lg border-l-4 border-green-400">
                      <h6 className="text-md font-semibold text-gray-700 flex items-center mb-4">
                        <MapPin className="w-4 h-4 mr-2" />
                        地址信息
                      </h6>

                      <div className="space-y-4">
                        <FormField
                          label="街道地址（第一行）"
                          value={contact.street_address_line1 || ''}
                          onChange={(e) => updateContact(index, 'street_address_line1', e.target.value.slice(0, 40))}
                          placeholder="456 University Ave"
                          maxLength={40}
                          required
                        />

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            街道地址（第二行）<span className="text-gray-500 italic ml-1">*可选</span>
                          </label>
                          <input
                            type="text"
                            value={contact.street_address_line2 || ''}
                            onChange={(e) => updateContact(index, 'street_address_line2', e.target.value.slice(0, 40))}
                            placeholder="Suite 789"
                            maxLength={40}
                            className="w-full h-12 px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent bg-gradient-to-r from-white to-green-50 shadow-sm hover:shadow-md transition-all duration-200"
                          />
                          <p className="text-xs text-gray-500 mt-1">最多40个字符</p>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <FormField
                            label="城市"
                            value={contact.city || ''}
                            onChange={(e) => updateContact(index, 'city', e.target.value.slice(0, 20))}
                            placeholder="Stanford"
                            maxLength={20}
                            required
                          />

                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              州/省份
                            </label>
                            <div className="flex items-center gap-3">
                              <input
                                type="text"
                                value={contact.state_province || ''}
                                onChange={(e) => updateContact(index, 'state_province', e.target.value.slice(0, 20))}
                                placeholder="CA"
                                maxLength={20}
                                disabled={contact.state_province_na}
                                className="flex-1 h-12 px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent bg-gradient-to-r from-white to-green-50 shadow-sm hover:shadow-md transition-all duration-200 disabled:bg-gray-100 disabled:text-gray-400"
                              />
                              <label className="flex items-center cursor-pointer">
                                <input
                                  type="checkbox"
                                  checked={contact.state_province_na || false}
                                  onChange={(e) => updateContact(index, 'state_province_na', e.target.checked)}
                                  className="mr-2 w-4 h-4 text-green-600 bg-white border-gray-300 rounded focus:ring-green-500 focus:ring-2"
                                />
                                <span className="text-sm text-gray-700">不适用</span>
                              </label>
                            </div>
                          </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              邮政编码
                            </label>
                            <div className="flex items-center gap-3">
                              <input
                                type="text"
                                value={contact.postal_code || ''}
                                onChange={(e) => updateContact(index, 'postal_code', e.target.value.slice(0, 10))}
                                placeholder="94305"
                                maxLength={10}
                                disabled={contact.postal_code_na}
                                className="flex-1 h-12 px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent bg-gradient-to-r from-white to-green-50 shadow-sm hover:shadow-md transition-all duration-200 disabled:bg-gray-100 disabled:text-gray-400"
                              />
                              <label className="flex items-center cursor-pointer">
                                <input
                                  type="checkbox"
                                  checked={contact.postal_code_na || false}
                                  onChange={(e) => updateContact(index, 'postal_code_na', e.target.checked)}
                                  className="mr-2 w-4 h-4 text-green-600 bg-white border-gray-300 rounded focus:ring-green-500 focus:ring-2"
                                />
                                <span className="text-sm text-gray-700">不适用</span>
                              </label>
                            </div>
                          </div>

                          <CountrySelect
                            label="国家/地区"
                            value={contact.country || ''}
                            onChange={(value) => updateContact(index, 'country', value)}
                            required
                          />
                        </div>
                      </div>
                    </div>

                    {/* 联系信息 */}
                    <div className="p-4 bg-gradient-to-r from-purple-50 to-pink-50 rounded-lg border-l-4 border-purple-400">
                      <h6 className="text-md font-semibold text-gray-700 flex items-center mb-4">
                        <Phone className="w-4 h-4 mr-2" />
                        联系信息
                      </h6>

                      <div className="space-y-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            电话号码
                          </label>
                          <div className="flex items-center gap-3">
                            <div className="flex-1">
                              <PhoneInput
                                value={contact.telephone || ''}
                                onChange={(value) => updateContact(index, 'telephone', value)}
                                placeholder="+1-650-123-4567"
                                disabled={contact.telephone_na}
                              />
                            </div>
                            <label className="flex items-center cursor-pointer">
                              <input
                                type="checkbox"
                                checked={contact.telephone_na || false}
                                onChange={(e) => updateContact(index, 'telephone_na', e.target.checked)}
                                className="mr-2 w-4 h-4 text-purple-600 bg-white border-gray-300 rounded focus:ring-purple-500 focus:ring-2"
                              />
                              <span className="text-sm text-gray-700">不适用</span>
                            </label>
                          </div>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            电子邮件地址
                          </label>
                          <div className="flex items-center gap-3">
                            <div className="flex-1">
                              <EmailInput
                                value={contact.email || ''}
                                onChange={(value) => updateContact(index, 'email', value)}
                                placeholder="zhang.wei@stanford.edu"
                                disabled={contact.email_na}
                              />
                            </div>
                            <label className="flex items-center cursor-pointer">
                              <input
                                type="checkbox"
                                checked={contact.email_na || false}
                                onChange={(e) => updateContact(index, 'email_na', e.target.checked)}
                                className="mr-2 w-4 h-4 text-purple-600 bg-white border-gray-300 rounded focus:ring-purple-500 focus:ring-2"
                              />
                              <span className="text-sm text-gray-700">不适用</span>
                            </label>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ));
            })()}
          </div>

          {/* 帮助信息 */}
          <div className="mt-6 p-4 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg border-l-4 border-blue-400">
            <h6 className="text-sm font-semibold text-blue-800 mb-2 flex items-center">
              <Mail className="w-4 h-4 mr-2" />
              填写说明
            </h6>
            <div className="text-sm text-blue-700 space-y-1">
              <p>• <strong>联系人数量：</strong> F1签证要求至少提供两个额外联系人</p>
              <p>• <strong>联系人类型：</strong> 可以是学术顾问、导师、教授、学校管理员等</p>
              <p>• <strong>地址要求：</strong> 请提供完整的美国地址信息</p>
              <p>• <strong>联系方式：</strong> 电话号码和电子邮件至少填写一项</p>
              <p>• <strong>字符限制：</strong> 姓氏和名字各最多33个字符，地址最多40个字符</p>
              <p>• <strong>不适用选项：</strong> 如果某些信息不适用，请勾选对应的"不适用"复选框</p>
            </div>
          </div>
        </div>

        {/* 预览信息 */}
        {data.additional_contacts && data.additional_contacts.length > 0 && (
          <div className="mt-6 p-4 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg border-l-4 border-blue-400 shadow-sm">
            <h4 className="font-semibold text-blue-800 mb-2">联系人信息预览</h4>
            <div className="text-sm text-blue-700 space-y-2">
              {data.additional_contacts.map((contact, index) => (
                <div key={index} className="p-3 bg-white rounded-lg border-l-2 border-blue-300">
                  <p><strong>联系人 #{index + 1}:</strong> {contact.surnames} {contact.given_name}</p>
                  {contact.street_address_line1 && (
                    <p><strong>地址:</strong> {contact.street_address_line1}
                      {contact.street_address_line2 && `, ${contact.street_address_line2}`}
                      {contact.city && `, ${contact.city}`}
                      {!contact.state_province_na && contact.state_province && `, ${contact.state_province}`}
                      {!contact.postal_code_na && contact.postal_code && ` ${contact.postal_code}`}
                      {contact.country && `, ${contact.country}`}
                    </p>
                  )}
                  {!contact.telephone_na && contact.telephone && (
                    <p><strong>电话:</strong> {contact.telephone}</p>
                  )}
                  {!contact.email_na && contact.email && (
                    <p><strong>邮箱:</strong> {contact.email}</p>
                  )}
                  {(contact.state_province_na || contact.postal_code_na || contact.telephone_na || contact.email_na) && (
                    <p className="text-xs text-orange-600">
                      <strong>不适用项:</strong> {
                        [
                          contact.state_province_na && '州/省份',
                          contact.postal_code_na && '邮政编码',
                          contact.telephone_na && '电话号码',
                          contact.email_na && '电子邮件'
                        ].filter(Boolean).join(', ')
                      }
                    </p>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default F1AdditionalContactsForm;
